/**
 * One site's asset register, plus the repeat-fault finding.
 *
 * "This reader has failed three times in ninety days, and the warranty labour
 * has passed the cost of the part" is a group-by here and three unrelated
 * service calls in a job-centric system. That is the whole argument for the
 * asset being the spine, so it gets a place on the page rather than a report.
 */
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { api } from "../lib/api";
import { day, money, relative, titleCase } from "../lib/format";
import { Card, Chip, DataTable, Empty, ErrorState, Loading, PageHeader, type Tone } from "../components/ui";
import type { AssetStatus } from "../lib/types";

const ASSET_TONE: Record<AssetStatus, Tone> = {
  healthy: "ok",
  degraded: "warn",
  fault: "crit",
  offline: "crit",
  retired: "neutral",
};

export default function SiteDetail() {
  const { id = "" } = useParams();

  const site = useQuery({ queryKey: ["site", id], queryFn: () => api.sites.get(id), enabled: Boolean(id) });
  const assets = useQuery({
    queryKey: ["assets", id],
    queryFn: () => api.assets.list({ site_id: id }),
    enabled: Boolean(id),
  });
  const faults = useQuery({ queryKey: ["repeat-faults"], queryFn: () => api.assets.repeatFaults() });

  if (site.isPending) return <Loading what="Loading the site" />;
  if (site.error) return <ErrorState error={site.error} onRetry={() => void site.refetch()} />;

  const siteFaults = (faults.data ?? []).filter((f) =>
    (assets.data ?? []).some((a) => a.id === f.asset_id),
  );

  return (
    <div className="page">
      <PageHeader eyebrow={site.data.customer_name} title={site.data.name}>
        {[site.data.address_line, site.data.suburb, site.data.state, site.data.postcode]
          .filter(Boolean)
          .join(", ")}
      </PageHeader>

      {(site.data.induction_required || site.data.swms_required) && (
        <p className="notice notice-warn">
          Before anyone starts here:{" "}
          {[
            site.data.induction_required ? "site induction" : null,
            site.data.swms_required ? "SWMS" : null,
          ]
            .filter(Boolean)
            .join(" and ")}{" "}
          required.
        </p>
      )}

      {siteFaults.map((fault) => (
        <div key={fault.asset_id} className="guard guard-warn" role="status">
          <b>
            {fault.asset_ref} has failed {fault.fault_count} times in {fault.window_days} days
          </b>
          <ul>
            <li>
              {money(fault.warranty_cost_cents)} of warranty labour
              {fault.replacement_cost_cents
                ? ` against a ${money(fault.replacement_cost_cents)} part`
                : ""}
              .
            </li>
            <li>{fault.recommendation}</li>
          </ul>
        </div>
      ))}

      <Card
        title="Asset register"
        subtitle={assets.data ? `${assets.data.length} registered` : undefined}
        padded={false}
      >
        {assets.isPending ? (
          <Loading what="Loading assets" />
        ) : assets.error ? (
          <ErrorState error={assets.error} onRetry={() => void assets.refetch()} />
        ) : assets.data.length === 0 ? (
          <Empty title="Nothing registered here yet">
            Accepting a quote pre-registers the hardware it will install, and the
            scan at install fills in the serials.
          </Empty>
        ) : (
          <DataTable
            columns={[
              { key: "ref", label: "Ref" },
              { key: "what", label: "Hardware" },
              { key: "where", label: "Location", hideOnPhone: true },
              { key: "status", label: "Status" },
              { key: "due", label: "Next test", align: "right", hideOnPhone: true },
            ]}
          >
            {assets.data.map((asset) => (
              <tr key={asset.id}>
                <td className="mono">{asset.ref}</td>
                <td>
                  {[asset.manufacturer, asset.model].filter(Boolean).join(" ") || "—"}
                  {asset.serial ? (
                    <div className="mono tiny muted">SN {asset.serial}</div>
                  ) : (
                    <div className="tiny muted">awaiting install scan</div>
                  )}
                </td>
                <td className="phone-hide">{asset.location_desc ?? "—"}</td>
                <td>
                  <Chip tone={ASSET_TONE[asset.status]} dot>
                    {titleCase(asset.status)}
                  </Chip>
                  {asset.standard_class && (
                    <div className="tiny muted">{asset.standard_class}</div>
                  )}
                </td>
                <td className="right phone-hide">
                  {asset.next_test_due_on ? (
                    <>
                      <div className="mono">{day(asset.next_test_due_on)}</div>
                      <div className="tiny muted">{relative(asset.next_test_due_on)}</div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </Card>
    </div>
  );
}
